import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOMServer from "react-dom/server"; // ⬅️ Import this at the top
import { UnControlled as CodeMirror } from "react-codemirror2";
import { useNavigate } from "react-router-dom";
import { useSwipeable } from "react-swipeable"; // Import the swipeable hook
import axios from "axios";
import { motion } from "framer-motion";
import "codemirror/lib/codemirror.css"; // Basic CodeMirror styles
import "codemirror/theme/material.css"; // You can pick other themes if you prefer
import "codemirror/mode/javascript/javascript"; // JavaScript mode (you can import other modes if necessary)
import "codemirror/mode/python/python"; // Python
import "codemirror/mode/clike/clike"; // Java, C++, C#
import "codemirror/mode/swift/swift"; // Swift
import "codemirror/addon/edit/closebrackets"; // Import auto-close brackets addon
import "codemirror/addon/edit/matchbrackets"; // Auto-indent on bracket closure
import confetti from "canvas-confetti";
import "./DragAndDropWorkspace.css";
import Swal from "sweetalert2"; // Import SweetAlert for pop-ups
import BeginnerTutorial from "./BeginnerTutorial";
import Split from "react-split";
import ProblemSection from "./ProblemSection";
import SolutionSection from "./SolutionSection";
import { render } from "react-dom";
import BlocksSection from "./BlocksSection";
import LevelSwitcher from "./LevelSwitcher";
import CodeEditor from "./CodeEditor";
import TestCases from "./TestCases";
import DemoTour from "./DemoTour";

const DragAndDropWorkspace = ({
  problem,
  solutions,
  level,
  setSelectedLevel,
  activeSolutionIndex,
  setActiveSolutionIndex,
  user,
  selectedLanguage,
  loading,
  isCompletionStatus,
  mode,
  setSolutionBlocks,
  setBlocks,
  blocks,
  setCodeSnippet,
  codeSnippet,
  solutionBlocks,
  handleLevelChange,
  updateProgress,
  solutionCompleted,
  activeSolutionName,
  resetSolutionCompleted,
}) => {
  // const correctSolution = problem?.correctSolution;
  // const initialBlocks = [];

  // const [blocks, setBlocks] = useState(initialBlocks); // Block library
  const [blocksByLevel, setBlocksByLevel] = useState({
    Beginner: [],
    Intermediate: [],
    Expert: [],
    Pro: [],
  }); // Persist available blocks for each level
  // const [solutionBlocks, setSolutionBlocks] = useState([]); // Solution section
  const [solutionBlocksByLevel, setSolutionBlocksByLevel] = useState({
    Beginner: [],
    Intermediate: [],
    Expert: [],
    Pro: [],
  }); // Track solution blocks for each level
  // const [codeSnippet, setCodeSnippet] = useState("");
  const [selectedContainerIndex, setSelectedContainerIndex] = useState(null); // Track selected container block index
  const [isSelectingBlock, setIsSelectingBlock] = useState(false); // Flag to show/hide block selection dropdown
  const [feedbackMessage, setFeedbackMessage] = useState(""); // Feedback message for solution check
  const [problemSolutions, setProblemSolutions] = useState([]); // Holds the solutions from the database
  // const [selectedLevel, setSelectedLevel] = useState(level); // State to manage the current level
  const [activePage, setActivePage] = useState(0); // To track swiping between pages
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedTab, setSelectedTab] = useState("Problem"); // New state for tab control
  const [selectedSolutionIndex, setSelectedSolutionIndex] = useState(0); // Index for solution tab
  const [highlightedBlock, setHighlightedBlock] = useState(null); // To track the highlighted block
  const [showHintButton, setShowHintButton] = useState(false); // Manage hint button visibility
  const [hintUsed, setHintUsed] = useState(false);
  const lastShuffledLevel = useRef(null); // Track the last shuffled level
  const [solutionBlocksBySolution, setSolutionBlocksBySolution] = useState({});
  const [blocksBySolution, setBlocksBySolution] = useState({});
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [areBlocksVisible, setAreBlocksVisible] = useState(level !== "Pro"); // Visibility state for blocks
  // const [proCodeSnippet, setProCodeSnippet] = useState("");
  const [selectedText, setSelectedText] = useState(""); // To store highlighted text
  const editorRef = useRef(null); // Ref to access the CodeMirror instance
  const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 }); // Button position
  const prevLanguage = useRef(selectedLanguage);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [allTestsPassed, setAllTestsPassed] = useState(null);
  const [currentTestIndex, setCurrentTestIndex] = useState(0); // Track the selected test case
  const [selectedTimeComplexity, setSelectedTimeComplexity] = useState("");
  const [selectedSpaceComplexity, setSelectedSpaceComplexity] = useState("");
  const [complexityFeedback, setComplexityFeedback] = useState(null);
  const [hoveredBlockIndex, setHoveredBlockIndex] = useState(null);
  const [shakeEditor, setShakeEditor] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [splitSizes, setSplitSizes] = useState([40, 60]);
  const [testCaseSizes, setTestCaseSizes] = useState([60, 40]);
  const [rightPaneSizes, setRightPaneSizes] = useState(
    level === "Pro" ? [0, 100] : [50, 50]
  );
  const [isChecking, setIsChecking] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const codeRunnerUrl =
    "https://blockcode-code-runner-923438747269.us-central1.run.app";

  // --- Helpers to manipulate blocks by path ---
  // path: e.g. [] = root, [2] = root[2], [1,0] = root[1].nestedBlocks[0]
  const DRAG_MIME = "application/x-blockcode";

  const cloneBlocks = (arr) => JSON.parse(JSON.stringify(arr));

  const getParentAndIndex = (root, path) => {
    if (path.length === 0) return { parent: null, index: -1 };
    let parent = { nestedBlocks: root };
    let key = "nestedBlocks";
    for (let i = 0; i < path.length - 1; i++) {
      // Ensure nestedBlocks exists and the index is valid
      if (!parent[key] || !Array.isArray(parent[key])) {
        console.error(`Invalid path in getParentAndIndex: nestedBlocks not found at path index ${i}`);
        return { parent: null, index: -1 };
      }
      const pathIndex = path[i];
      if (pathIndex < 0 || pathIndex >= parent[key].length) {
        console.error(`Invalid path in getParentAndIndex: index ${pathIndex} out of bounds at path level ${i}`);
        return { parent: null, index: -1 };
      }
      const nextParent = parent[key][pathIndex];
      if (!nextParent) {
        console.error(`Invalid path in getParentAndIndex: block not found at path index ${i}, index ${pathIndex}`);
        return { parent: null, index: -1 };
      }
      parent = nextParent;
      key = "nestedBlocks";
    }
    const index = path[path.length - 1];
    return { parent, index };
  };

  const removeBlockAtPath = (rootBlocks, path) => {
    const draft = cloneBlocks(rootBlocks);
    if (path.length === 0) return { updated: draft, removed: null };
    if (path.length === 1) {
      const idx = path[0];
      if (idx < 0 || idx >= draft.length) {
        console.error(`Invalid path in removeBlockAtPath: index ${idx} out of bounds`);
        return { updated: draft, removed: null };
      }
      const removed = draft[idx];
      draft.splice(idx, 1);
      return { updated: draft, removed };
    }
    const { parent, index } = getParentAndIndex(draft, path);
    if (!parent || index < 0) {
      console.error(`Invalid path in removeBlockAtPath: parent or index invalid`);
      return { updated: draft, removed: null };
    }
    if (!parent.nestedBlocks || !Array.isArray(parent.nestedBlocks)) {
      console.error(`Invalid path in removeBlockAtPath: parent.nestedBlocks is not an array`);
      return { updated: draft, removed: null };
    }
    if (index >= parent.nestedBlocks.length) {
      console.error(`Invalid path in removeBlockAtPath: index ${index} out of bounds for nestedBlocks length ${parent.nestedBlocks.length}`);
      return { updated: draft, removed: null };
    }
    const removed = parent.nestedBlocks[index];
    parent.nestedBlocks.splice(index, 1);
    return { updated: draft, removed };
  };

  const insertIntoParentAtIndex = (rootBlocks, parentPath, index, block) => {
    const draft = cloneBlocks(rootBlocks);
    if (parentPath.length === 0) {
      const out = [...draft];
      out.splice(index, 0, block);
      return out;
    }
    let parent = { nestedBlocks: draft };
    for (let i = 0; i < parentPath.length; i++) {
      // Ensure nestedBlocks exists and the index is valid
      if (!parent.nestedBlocks || !Array.isArray(parent.nestedBlocks)) {
        console.error(`Invalid path: nestedBlocks not found at path index ${i}`);
        return draft; // Return unchanged if path is invalid
      }
      const pathIndex = parentPath[i];
      if (pathIndex < 0 || pathIndex >= parent.nestedBlocks.length) {
        console.error(`Invalid path: index ${pathIndex} out of bounds at path level ${i}`);
        return draft; // Return unchanged if index is out of bounds
      }
      const nextParent = parent.nestedBlocks[pathIndex];
      if (!nextParent) {
        console.error(`Invalid path: block not found at path index ${i}, index ${pathIndex}`);
        return draft; // Return unchanged if block doesn't exist
      }
      // Ensure the block has nestedBlocks initialized if it's a container
      if (nextParent.isContainer && !nextParent.nestedBlocks) {
        nextParent.nestedBlocks = [];
      }
      parent = nextParent;
    }
    // If we have a parentPath, the final parent should be a container (or root)
    // Ensure parent has nestedBlocks array initialized
    // If parent doesn't have nestedBlocks but is a container, initialize it
    if (parent.isContainer && (!parent.nestedBlocks || !Array.isArray(parent.nestedBlocks))) {
      parent.nestedBlocks = [];
    }
    // Final safety check: ensure nestedBlocks exists before inserting
    if (!parent.nestedBlocks || !Array.isArray(parent.nestedBlocks)) {
      console.error(`Invalid insert: parent at path does not have nestedBlocks array`);
      return draft; // Return unchanged if parent doesn't have nestedBlocks
    }
    parent.nestedBlocks.splice(index, 0, block);
    return draft;
  };

  const insertRelative = (
    root,
    parentPath,
    targetIndex,
    dropAfter,
    block,
    movedPath = null
  ) => {
    // Adjust target index if moving within the same array and from an earlier index
    let insertIndex = targetIndex + (dropAfter ? 1 : 0);

    if (
      movedPath &&
      movedPath.length === parentPath.length &&
      movedPath.slice(0, -1).every((v, i) => v === parentPath[i]) &&
      movedPath[movedPath.length - 1] < insertIndex
    ) {
      insertIndex -= 1;
    }
    return insertIntoParentAtIndex(root, parentPath, insertIndex, block);
  };

  // Adjust container path after a block has been removed
  // If the removed block was before the container at any level, decrement the container's index at that level
  const adjustPathAfterRemoval = (containerPath, removedPath) => {
    console.log(`adjustPathAfterRemoval: containerPath=${JSON.stringify(containerPath)}, removedPath=${JSON.stringify(removedPath)}`);
    
    // If paths are the same, container path is invalid (we're removing the container itself)
    if (containerPath.length === removedPath.length && 
        containerPath.every((v, i) => v === removedPath[i])) {
      console.warn("adjustPathAfterRemoval: Container path and removed path are the same");
      return null; // Invalid - can't append to removed container
    }
    
    // If removed block is nested inside container, no adjustment needed
    // Check if removedPath starts with containerPath (removed is inside container)
    if (removedPath.length > containerPath.length) {
      const containerPrefix = removedPath.slice(0, containerPath.length);
      if (containerPath.every((v, i) => v === containerPrefix[i])) {
        console.log("adjustPathAfterRemoval: Removed block is nested inside container, no adjustment needed");
        return containerPath; // No change - removal was inside container
      }
    }
    
    // If container is nested inside removed block's subtree, invalid
    if (containerPath.length > removedPath.length) {
      const removedPrefix = containerPath.slice(0, removedPath.length);
      if (removedPath.every((v, i) => v === removedPrefix[i])) {
        console.warn("adjustPathAfterRemoval: Container is nested inside removed block, invalid");
        return null; // Invalid - container is inside removed block
      }
    }
    
    // Find the first level where paths diverge
    let firstDiffLevel = 0;
    const minLength = Math.min(containerPath.length, removedPath.length);
    while (firstDiffLevel < minLength &&
           containerPath[firstDiffLevel] === removedPath[firstDiffLevel]) {
      firstDiffLevel++;
    }
    
    console.log(`adjustPathAfterRemoval: First differing level is ${firstDiffLevel}, minLength=${minLength}`);
    
    // If we found a differing level and both paths have that level
    if (firstDiffLevel < minLength) {
      const removedIndex = removedPath[firstDiffLevel];
      const containerIndex = containerPath[firstDiffLevel];
      
      console.log(`adjustPathAfterRemoval: At level ${firstDiffLevel}, removedIndex=${removedIndex}, containerIndex=${containerIndex}`);
      
      // If removed block comes before container at this level, adjust the container index
      if (removedIndex < containerIndex) {
        const adjustedPath = [...containerPath];
        adjustedPath[firstDiffLevel] = containerIndex - 1;
        console.log(`adjustPathAfterRemoval: ✓ Adjusted container path from [${containerPath.join(',')}] to [${adjustedPath.join(',')}]`);
        
        return adjustedPath;
      } else {
        console.log(`adjustPathAfterRemoval: No adjustment needed (removedIndex ${removedIndex} >= containerIndex ${containerIndex})`);
      }
    } else if (firstDiffLevel === minLength) {
      // Paths share a common prefix
      // If container path is longer, it's nested deeper - no adjustment needed at shared levels
      // If removed path is longer, it's nested inside container (already handled above)
      console.log(`adjustPathAfterRemoval: Paths share common prefix of length ${firstDiffLevel}, no adjustment needed`);
    }
    
    // No adjustment needed
    console.log(`adjustPathAfterRemoval: Returning original container path [${containerPath.join(',')}]`);
    return containerPath;
  };

  const appendIntoContainer = (rootBlocks, containerPath, block) => {
    console.log("appendIntoContainer called with:", { containerPath, blockContent: block?.content, rootBlocksLength: rootBlocks?.length });
    
    // If containerPath is empty, we're appending to root
    if (containerPath.length === 0) {
      console.warn("appendIntoContainer called with empty path, appending to root");
      return [...rootBlocks, block];
    }
    
    // Recursive function to navigate and create new object references
    const updateBlockAtPath = (blocks, path, blockToAdd, depth = 0) => {
      console.log(`updateBlockAtPath: depth=${depth}, path=${JSON.stringify(path)}, blocks.length=${blocks?.length}`);
      
      if (depth === path.length) {
        // This shouldn't happen in appendIntoContainer
        console.error(`updateBlockAtPath: Reached end of path without finding container (depth ${depth} == path length ${path.length})`);
        return blocks;
      }
      
      const pathIndex = path[depth];
      console.log(`updateBlockAtPath: At depth ${depth}, accessing index ${pathIndex} of array with length ${blocks?.length}`);
      
      // Validate index
      if (!Array.isArray(blocks)) {
        console.error(`updateBlockAtPath: blocks is not an array at depth ${depth}`);
        return blocks;
      }
      
      if (pathIndex < 0 || pathIndex >= blocks.length) {
        console.error(`updateBlockAtPath: ✗ Invalid container path: index ${pathIndex} out of bounds at depth ${depth}, array length: ${blocks.length}`);
        console.error(`updateBlockAtPath: Available indices: 0-${blocks.length - 1}, blocks:`, blocks.map((b, i) => `${i}:${b?.content || 'undefined'}`));
        return blocks;
      }
      
      const currentBlock = blocks[pathIndex];
      if (!currentBlock) {
        console.error(`updateBlockAtPath: ✗ Block not found at depth ${depth}, index ${pathIndex}`);
        return blocks;
      }
      
      console.log(`updateBlockAtPath: Found block at depth ${depth}, index ${pathIndex}: "${currentBlock.content}", isContainer: ${currentBlock.isContainer}`);
      
      // If this is the target container (last in path)
      if (depth === path.length - 1) {
        console.log(`updateBlockAtPath: ✓ Reached target container: "${currentBlock.content}", appending block: "${blockToAdd.content}"`);
        console.log(`updateBlockAtPath: Current nestedBlocks:`, currentBlock.nestedBlocks?.map(b => b.content) || 'none');
        
        // Create a new block object with updated nestedBlocks
        const updatedNestedBlocks = [...(currentBlock.nestedBlocks || []), blockToAdd];
        const updatedBlock = {
          ...currentBlock,
          nestedBlocks: updatedNestedBlocks
        };
        // Create a new blocks array with the updated block
        const newBlocks = [...blocks];
        newBlocks[pathIndex] = updatedBlock;
        console.log(`updateBlockAtPath: ✓ Updated container nestedBlocks length: ${updatedNestedBlocks.length}, new blocks:`, updatedNestedBlocks.map(b => b.content));
        return newBlocks;
      } else {
        // Continue navigating deeper - recursively update nested blocks
        const originalNestedBlocks = currentBlock.nestedBlocks;
        console.log(`updateBlockAtPath: Continuing to next level, nestedBlocks length: ${originalNestedBlocks?.length || 0}`);
        const updatedNestedBlocks = updateBlockAtPath(
          originalNestedBlocks || [],
          path,
          blockToAdd,
          depth + 1
        );
        
        // Check if the recursive call failed (returned unchanged reference)
        // If originalNestedBlocks is undefined, we passed [] to the recursive call
        // In that case, if it returns the same [] reference, it failed
        const expectedOriginal = originalNestedBlocks || [];
        if (updatedNestedBlocks === expectedOriginal && originalNestedBlocks !== undefined) {
          console.error(`updateBlockAtPath: ✗ Recursive call at depth ${depth + 1} failed (returned unchanged reference), returning unchanged`);
          return blocks;
        }
        
        // Create a new block object with updated nestedBlocks
        const updatedBlock = {
          ...currentBlock,
          nestedBlocks: updatedNestedBlocks
        };
        // Create a new blocks array with the updated block
        const newBlocks = [...blocks];
        newBlocks[pathIndex] = updatedBlock;
        console.log(`updateBlockAtPath: ✓ Updated intermediate block at depth ${depth}, continuing`);
        return newBlocks;
      }
    };
    
    const result = updateBlockAtPath(rootBlocks, containerPath, block, 0);
    
    // Check if the operation succeeded by comparing array references
    if (result === rootBlocks) {
      console.error(`appendIntoContainer: ✗ Failed - returned unchanged rootBlocks`);
    } else {
      console.log(`appendIntoContainer: ✓ Success - returned modified blocks`);
    }
    
    return result;
  };

  // Flatten a container into itself + all nested (with nestedBlocks cleared)
  const flattenBlock = (block) => {
    const out = [];
    const walk = (b) => {
      const shallow = { ...b, nestedBlocks: [] };
      out.push(shallow);
      (b.nestedBlocks || []).forEach(walk);
    };
    walk(block);
    return out;
  };

  // Dedup by id
  const dedupById = (arr) => {
    const seen = new Set();
    return arr.filter((b) => (seen.has(b.id) ? false : (seen.add(b.id), true)));
  };

  // Drag payload helpers
  const setDragData = (e, payload) => {
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "move";
    setIsDragging(true);
  };
  const getDragData = (e) => {
    try {
      return JSON.parse(e.dataTransfer.getData(DRAG_MIME));
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (loading || !problem || !solutions) return; // Skip if data is still loading or not available

    // if (level === "Pro" && areBlocksVisible) {
    //   return; // Do not overwrite blocks for Pro level if already revealed
    // }

    setTestResults([]); // Reset test cases whenever level or solution changes
    setAllTestsPassed(null); // Reset test status
    setCurrentTestIndex(0); // Reset to the first test case

    // Utility function to shuffle the blocks
    const shuffleArray = (array) => {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    };

    // Retrieve saved state from local storage
    const getSavedState = (key) => {
      const savedState = localStorage.getItem(key);
      return savedState ? JSON.parse(savedState) : null;
    };

    // Save state to local storage
    const saveState = (key, data) => {
      localStorage.setItem(key, JSON.stringify(data));
    };

    // Unique key for local storage based on level and solution
    const storageKey = `solution-${problem.id}-${level}-${activeSolutionIndex}-${selectedLanguage}`;

    if (level === "Pro") {
      // // Retrieve and set the saved Pro level code snippet
      // const proStorageKey = `pro-code-${problem.id}-${selectedLanguage}`;
      // const savedProCode = localStorage.getItem(proStorageKey) || "";
      // setCodeSnippet(savedProCode);
      // return; // Exit early for Pro level
    }

    const restoreStateForLevel = () => {
      if (level === "Pro") {
        setSolutionBlocks([]); // Pro level doesn't use solution blocks
        setBlocks([]);
        return; // Pro level doesn't display blocks
        // const proStorageKey = `pro-code-${problem.id}-${selectedLanguage}`;
        // const savedProCode = localStorage.getItem(proStorageKey) || "";
        // setCodeSnippet(savedProCode);
        // return;
      }

      // Load saved state if available
      const savedState = getSavedState(storageKey);
      if (savedState) {
        setSolutionBlocks(savedState.solutionBlocks || []);
        setBlocks(savedState.blocks || []);
        setCodeSnippet(savedState.codeSnippet || "");
        return;
      }

      // If no saved state, set up default blocks
      const filteredSolutions = solutions.filter(
        (solution) => solution.language === selectedLanguage.toUpperCase()
      );

      if (!filteredSolutions.length) {
        setBlocks([]);
        setSolutionBlocks([]);
        setCodeSnippet("");
        return;
      }

      if (level === "Beginner") {
        const optimalSolution = filteredSolutions.find(
          (solution) => solution.isOptimal
        );

        if (optimalSolution) {
          const beginnerBlocks = optimalSolution.blocks.filter(
            (block) => block.level === "BEGINNER"
          );

          const shuffledBlocks = shuffleArray([...beginnerBlocks]);
          setBlocks(shuffledBlocks);
          setSolutionBlocks([]);
          setCodeSnippet("");
        }
      } else if (level === "Intermediate" || level === "Expert") {
        const solution = filteredSolutions[activeSolutionIndex];
        if (solution) {
          const blockLevel = solution.isOptimal
            ? level === "Intermediate"
              ? "BEGINNER"
              : "EXPERT"
            : level.toUpperCase();

          const levelBlocks = solution.blocks.filter(
            (block) => block.level === blockLevel
          );

          const shuffledBlocks = shuffleArray([...levelBlocks]);
          setBlocks(shuffledBlocks);
          setSolutionBlocks([]);
          setCodeSnippet("");
        }
      }
    };

    restoreStateForLevel();
  }, [
    level,
    problem,
    solutions,
    activeSolutionIndex,
    selectedLanguage,
    loading,
    areBlocksVisible,
  ]);

  // Save the blocks and solutions whenever they change
  useEffect(() => {
    if (!problem || level === "Pro") return;

    // prevent saving empty state accidentally
    if (
      blocks.length === 0 &&
      solutionBlocks.length === 0 &&
      codeSnippet === ""
    ) {
      return;
    }

    const storageKey = `solution-${problem.id}-${level}-${activeSolutionIndex}-${selectedLanguage}`;

    const stateToSave = {
      solutionBlocks,
      blocks,
      codeSnippet,
    };

    localStorage.setItem(storageKey, JSON.stringify(stateToSave));
  }, [
    solutionBlocks,
    blocks,
    codeSnippet,
    level,
    problem,
    activeSolutionIndex,
    selectedLanguage,
  ]);

  const countAllBlocks = (blocks) => {
    let count = 0;

    for (const block of blocks) {
      count += 1;
      if (block.children && Array.isArray(block.children)) {
        count += countAllBlocks(block.children);
      }
      if (block.nestedBlocks && Array.isArray(block.nestedBlocks)) {
        count += countAllBlocks(block.nestedBlocks);
      }
    }

    return count;
  };

  useEffect(() => {
    const totalBlocks = countAllBlocks(solutionBlocks);
    console.log("i just ran");
    if (totalBlocks < 3) {
      setSplitSizes([45, 55]);
    } else if (totalBlocks <= 4) {
      setSplitSizes([50, 50]);
    } else if (totalBlocks <= 5) {
      setSplitSizes([60, 40]);
    } else if (totalBlocks <= 6) {
      setSplitSizes([65, 35]);
    } else {
      setSplitSizes([75, 25]);
    }
  }, [solutionBlocks]);

  // Helper function to collect all blocks (including nested ones) in the solution section
  const collectAllBlocksFromSolution = (blocks) => {
    const allBlocks = [];

    const collectBlocks = (blockList) => {
      blockList.forEach((block) => {
        allBlocks.push(block);
        if (block.nestedBlocks?.length > 0) {
          collectBlocks(block.nestedBlocks);
        }
      });
    };

    collectBlocks(blocks);
    return allBlocks;
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const resetWorkspace = () => {
    const filteredSolutions = solutions.filter(
      (solution) => solution.language === selectedLanguage.toUpperCase()
    );

    let initialBlocks = [];

    if (level === "Beginner") {
      const optimalSolution = filteredSolutions.find(
        (solution) => solution.isOptimal
      );
      if (optimalSolution) {
        initialBlocks = optimalSolution.blocks.filter(
          (block) => block.level === "BEGINNER"
        );
      }
    } else if (level === "Intermediate" || level === "Expert") {
      const currentSolution = filteredSolutions[activeSolutionIndex];
      if (currentSolution) {
        const blockLevel = currentSolution.isOptimal
          ? level === "Intermediate"
            ? "BEGINNER"
            : "EXPERT"
          : level.toUpperCase();
        initialBlocks = currentSolution.blocks.filter(
          (block) => block.level === blockLevel
        );
      }
    }

    const reshuffledBlocks = shuffleArray(initialBlocks);

    setBlocks(reshuffledBlocks); // 🔁 Reshuffled blocks
    setSolutionBlocks([]); // 🧼 Clear solution
    setCodeSnippet(""); // 🧹 Clear code
    setTestResults([]); // 🚫 Clear test results
    setAllTestsPassed(null); // 🚫 Reset pass status
    setCurrentTestIndex(0); // 📍 Reset to first test
    setShowHintButton(false); // Hide hint
    setHintUsed(false); // Reset hint flag
    resetSolutionCompleted();

    // Optional: Clear localStorage for this level+solution
    const storageKey = `solution-${problem.id}-${level}-${activeSolutionIndex}-${selectedLanguage}`;
    localStorage.removeItem(storageKey);
  };

  const optimalSolution = solutions.find((solution) => solution.isOptimal);

  const handleSelectionChange = (editor) => {
    const selection = editor.getSelection(); // Get the selected text
    if (selection.trim()) {
      // Get the cursor position in pixels
      const cursorCoords = editor.cursorCoords(true, "window");
      setButtonPosition({
        x: cursorCoords.left,
        y: cursorCoords.bottom + 5, // Slightly below the cursor
      });
    }
    setSelectedText(selection);
  };

  const handleWhatDoesThisMean = async () => {
    if (!selectedText.trim()) {
      Swal.fire({
        title: "No Code Selected",
        text: "Please highlight some code to get an explanation.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      // Show loading modal
      Swal.fire({
        title: "Processing...",
        text: "Fetching an explanation for the selected code.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Send API request to the backend
      const response = await axios.post(
        `${baseUrl}/problems/explain`, // Update this URL as per your setup
        {
          code: selectedText,
          language: selectedLanguage,
        }
      );

      const explanation = response.data.explanation;

      const cleanedExplanation = explanation
        .split("\n")
        .map((line) => line.trimStart()) // Remove leading spaces per line
        .join("\n")
        .trim(); // Remove overall leading/trailing whitespace

      // Show explanation in a popup
      Swal.fire({
        title: "💡 Code Explanation",
        html: `
          <div style="
            font-family: 'JetBrains Mono', monospace;
            font-size: 14px;
            line-height: 1.6;
            background-color: #f9f9f9;
            color: #333;
            padding: 16px;
            border-radius: 12px;
            border: 1px solid #ddd;
            max-height: 400px;
            overflow-y: auto;
            white-space: normal;
            text-align: left;
            margin: 0;
          ">
            ${cleanedExplanation}
          </div>
        `,
        background: "#ffffff",
        confirmButtonText: "Close",
        confirmButtonColor: "#4CAF50",
        customClass: {
          popup: "custom-swal-explanation my-positioned-popup",
        },
        width: "600px",
        position: "top",
      });
    } catch (error) {
      console.error("Error explaining code:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch explanation. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // Handle level change to reset blocks visibility
  useEffect(() => {
    if (level === "Pro") {
      setAreBlocksVisible(false); // Hide blocks for Pro level
    } else {
      setAreBlocksVisible(true); // Show blocks for other levels
    }
  }, [level]);

  const handleRevealBlocks = () => {
    Swal.fire({
      title: "Reminder!",
      text: "Blocks on the Pro Level will not generate any code; they are just here to guide you through your own hand-coded solution.",
      icon: "info",
      showDenyButton: true, // Adds a second button
      confirmButtonText: "Reveal Blocks",
      denyButtonText: "Try Without Revealing",
      customClass: {
        popup: "pro-level-popup", // Optional: Add custom styling
      },
    }).then((result) => {
      if (result.isConfirmed) {
        // Reveal blocks if the user clicks "Reveal Blocks"
        const optimalSolution = solutions.find(
          (solution) =>
            solution.isOptimal &&
            solution.language === selectedLanguage.toUpperCase()
        );

        if (optimalSolution) {
          // Extract Expert blocks for the optimal solution
          const expertBlocks = optimalSolution.blocks.filter(
            (block) => block.level === "EXPERT"
          );

          // Set the solution blocks to the Expert blocks
          // setSolutionBlocks(expertBlocks);

          // Set the blocks to render in the blocks library
          setBlocks(expertBlocks);

          // Update the code snippet (if needed for Pro level guidance)
          // setCodeSnippet(generateCodeFromBlocks(expertBlocks));
        }

        setAreBlocksVisible(true); // Make the blocks visible
      } else if (result.isDenied) {
        // Just close the popup without revealing blocks
      }
    });
  };

  // // useEffect for Tutorial Screen
  // useEffect(() => {
  //   if (level === "Beginner" && !user?.isLoggedIn) {
  //     const hasSeenTutorial = localStorage.getItem("beginnerTutorialSeen");
  //     if (!hasSeenTutorial) {
  //       setShowTutorial(true);
  //     }
  //   }
  // }, [level, user]);

  const handleNextStep = () => {
    if (tutorialStep < 2) {
      setTutorialStep(tutorialStep + 1);
    } else {
      handleCloseTutorial();
    }
  };

  const handleCloseTutorial = () => {
    setShowTutorial(false);
    localStorage.setItem("beginnerTutorialSeen", "true"); // Mark tutorial as seen
    setTutorialStep(0); // Reset tutorial step
  };

  const blockHighlightClass =
    showTutorial && tutorialStep === 0 ? "highlight" : "";
  const solutionHighlightClass =
    showTutorial && tutorialStep === 1 ? "highlight" : "";
  const codeSnippetHighlightClass =
    showTutorial && tutorialStep === 2 ? "highlight" : "";

  const fetchHint = async () => {
    if (
      !problem ||
      !codeSnippet ||
      !solutionBlocks ||
      !blocks ||
      !problem?.solutions?.[activeSolutionIndex]?.solutionCode
    )
      return;

    try {
      const response = await axios.post(`${baseUrl}/problems/generateHint`, {
        problemName: problem.title,
        codeSnippet: generateDynamicCode(
          problem.solutions[activeSolutionIndex]?.solutionCode,
          solutionBlocks
        ),
        solutionBlocks,
        blocks,
        solutionCode: problem.solutions[activeSolutionIndex].solutionCode,
      });

      const rawHint = response.data?.hint;

      if (rawHint) {
        // ✅ Normalize text for loose matching
        const normalize = (str) =>
          str
            .toLowerCase()
            .replace(/[^a-z0-9]+/gi, "")
            .trim();

        const allBlocks = [...solutionBlocks, ...blocks];

        // ✅ Find first block that closely matches the hint
        const matchedBlock = allBlocks.find((block) =>
          normalize(rawHint).includes(normalize(block.content))
        );

        let formattedHint = rawHint;

        if (matchedBlock) {
          const blockStyle = getBlockStyle(matchedBlock.type);

          const renderedBlockHTML = ReactDOMServer.renderToString(
            <div
              style={{
                pointerEvents: "none",
                cursor: "default",
                display: "inline-block",
                fontSize: "13px",
                transform: "scale(0.95)",
                backgroundColor: blockStyle.backgroundColor,
                color: blockStyle.color,
                borderRadius: "10px",
                padding: "6px 10px",
                whiteSpace: "normal",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              {matchedBlock.content}
            </div>
          );

          // ✅ Escape special characters in block content for regex
          const escapedBlockContent = matchedBlock.content.replace(
            /[-/\\^$*+?.()|[\]{}]/g,
            "\\$&"
          );

          // ✅ Match with or without quotes (single or double), case-insensitive
          const regex = new RegExp(`(["'\`])?${escapedBlockContent}\\1?`, "gi");

          formattedHint = rawHint.replace(regex, renderedBlockHTML);
        }

        // ✅ Show popup with formatted hint
        Swal.fire({
          title: "Hint",
          html: `
                <div style="text-align: left; line-height: 1.5; font-size: 15px;">
                  ${formattedHint}
                </div>
              `,
          icon: "info",
          confirmButtonText: "Got it!",
          customClass: {
            popup: "hint-popup",
          },
        });
      } else {
        Swal.fire({
          title: "No Hint Available",
          text: "Unable to generate a hint at this time. Please try again later.",
          icon: "warning",
          confirmButtonText: "Okay",
        });
      }
    } catch (error) {
      console.error("Error fetching hint:", error);
      Swal.fire({
        title: "Error",
        text: "An error occurred while fetching the hint. Please try again.",
        icon: "error",
        confirmButtonText: "Okay",
      });
    }
  };

  // // Function to handle the Hint button click
  // const handleHint = () => {
  //   if (level === "Beginner") {
  //     const nextBlock = findNextBlockToHighlight(optimalSolution);
  //     if (nextBlock) {
  //       setHighlightedBlock(nextBlock.content); // Highlight the next block
  //       setTimeout(() => {
  //         setHighlightedBlock(null); // Remove highlight after 2 seconds
  //       }, 2000);
  //     }
  //   } else if (level === "Intermediate") {
  //     const activeSolution = solutions[activeSolutionIndex]; // Get the currently active solution
  //     const nextBlock = findNextBlockToHighlight(activeSolution);
  //     if (nextBlock) {
  //       setHighlightedBlock(nextBlock.content); // Highlight the next block
  //       setTimeout(() => {
  //         setHighlightedBlock(null); // Remove highlight after 2 seconds
  //       }, 2000);
  //     }
  //   }
  // };

  // // Function to find the next block to highlight
  // const findNextBlockToHighlight = (solution) => {
  //   if (!solution || !solution.blocks) {
  //     return null;
  //   }

  //   // Find the first block in the selected solution that hasn't been added yet
  //   for (let block of solution.blocks) {
  //     const isAlreadyInSolution = solutionBlocks.some(
  //       (solBlock) => solBlock.content === block.content
  //     );
  //     if (!isAlreadyInSolution) {
  //       return block; // Return the next block to be added
  //     }
  //   }
  //   return null; // No more blocks to suggest
  // };

  // const renderSolutionTabs = () => {
  //   // Render tabs for Intermediate and Expert levels
  //   if (
  //     (level === "Intermediate" || level === "Expert") &&
  //     solutions.length > 1
  //   ) {
  //     return (
  //       <div className="solution-tabs">
  //         {solutions.map((solution, index) => (
  //           <button
  //             key={index}
  //             className={`solution-tab ${
  //               index === activeSolutionIndex ? "active" : ""
  //             }`}
  //             onClick={() => setActiveSolutionIndex(index)}
  //           >
  //             {solution?.name}
  //             {/* {solution.isOptimal ? "Optimal" : "Non-optimal"} */}
  //           </button>
  //         ))}
  //       </div>
  //     );
  //   }
  //   return null; // Return null if the conditions are not met
  // };

  // Add event listener to handle window resize for dynamic responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Swipe handlers for mobile
  const handlers = useSwipeable({
    onSwipedLeft: () => setActivePage(1), // Swipe left to show solution
    onSwipedRight: () => setActivePage(0), // Swipe right to show problem
    preventDefaultTouchmoveEvent: true,
    trackMouse: true, // Allows swipe on desktop with mouse drag
  });

  // Handle tab change
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  // CodeMirror configuration
  const options = {
    mode:
      selectedLanguage === "javascript"
        ? "javascript"
        : selectedLanguage === "python"
          ? "python"
          : selectedLanguage === "java"
            ? "text/x-java"
            : selectedLanguage === "csharp"
              ? "text/x-csharp" // Correct for C#
              : selectedLanguage === "cpp"
                ? "text/x-c++src" // Correct for C++
                : selectedLanguage === "swift"
                  ? "text/x-swift" // Swift mode
                  : "javascript", // Fallback to JavaScript
    theme: "material",
    lineNumbers: true,
    lineWrapping: true,
    readOnly: level !== "Pro", // Editable only on Pro level
    ...(level === "Pro" && {
      autoCloseBrackets: true, // Enables automatic closing of brackets
      matchBrackets: true, // Auto-indents when pressing Enter after a bracket
    }),
  };

  const addToSolution = (blockContent) => {
    const block = blocks?.find((b) => b.content === blockContent);
    if (block) {
      // Check if the block already exists in the solution
      const blockExists = solutionBlocks.some((b) => b.id === block.id);
      if (!blockExists) {
        const newBlock = block.isContainer
          ? { ...block, nestedBlocks: block.nestedBlocks || [] }
          : block;

        // Add the block to the solution section
        setSolutionBlocks((prev) => {
          const updatedSolutionBlocks = [...prev, newBlock];

          // Update the specific solution's blocks while preserving nested blocks
          if (level === "Intermediate" || level === "Expert") {
            const solutionKey = `${level}-${activeSolutionIndex}`;
            setSolutionBlocksBySolution((prevSolution) => {
              const existingSolutionBlocks = prevSolution[solutionKey] || [];
              const mergedBlocks = mergeNestedBlocks(
                existingSolutionBlocks,
                updatedSolutionBlocks
              );
              return {
                ...prevSolution,
                [solutionKey]: mergedBlocks,
              };
            });
          }

          // Update the code snippet only if the level is not "Pro"
          if (level !== "Pro") {
            setCodeSnippet(generateCodeFromBlocks(updatedSolutionBlocks));
          }

          return updatedSolutionBlocks;
        });

        // Collect all blocks from the added block, including nested blocks
        const allBlocksToRemove = collectAllBlocksFromSolution([block]);

        // Remove the block and all its nested blocks from the blocks section
        setBlocks((prevBlocks) =>
          prevBlocks.filter(
            (b) => !allBlocksToRemove.some((nested) => nested.id === b.id)
          )
        );

        // Update the block library for Intermediate/Expert levels
        if (level === "Intermediate" || level === "Expert") {
          const solutionKey = `${level}-${activeSolutionIndex}`;
          setBlocksBySolution((prev) => ({
            ...prev,
            [solutionKey]: prev[solutionKey]?.filter(
              (b) => !allBlocksToRemove.some((nested) => nested.id === b.id)
            ),
          }));
        }
      }
    }
  };

  //arrow functions for blocks on solution board
  const moveBlockUp = (index) => {
    if (index <= 0 || solutionBlocks.length === 0) return; // No unnecessary updates

    setSolutionBlocks((prev) => {
      const newOrder = [...prev];
      [newOrder[index], newOrder[index - 1]] = [
        newOrder[index - 1],
        newOrder[index],
      ];

      // Check if the new order is the same as the old one
      if (JSON.stringify(prev) === JSON.stringify(newOrder)) return prev;

      setCodeSnippet(generateCodeFromBlocks(newOrder)); // Update code snippet only when necessary
      return newOrder; // Return updated order
    });
  };

  const moveBlockDown = (index) => {
    if (index >= solutionBlocks.length - 1 || solutionBlocks.length === 0)
      return; // No unnecessary updates

    setSolutionBlocks((prev) => {
      const newOrder = [...prev];
      [newOrder[index], newOrder[index + 1]] = [
        newOrder[index + 1],
        newOrder[index],
      ];

      // Check if the new order is the same as the old one
      if (JSON.stringify(prev) === JSON.stringify(newOrder)) return prev;

      setCodeSnippet(generateCodeFromBlocks(newOrder)); // Update code snippet only when necessary
      return newOrder; // Return updated order
    });
  };

  const moveNestedUp = (containerIndexPath, blockIndex) => {
    if (blockIndex <= 0) return; // Prevent moving the first block up

    setSolutionBlocks((prev) => {
      const recursiveMove = (blocks, path) => {
        const [currentIndex, ...remainingPath] = path;

        return blocks.map((block, index) => {
          if (index === currentIndex) {
            if (remainingPath.length === 0) {
              // Perform the move within the nestedBlocks array
              const newNestedBlocks = [...block.nestedBlocks];
              [newNestedBlocks[blockIndex - 1], newNestedBlocks[blockIndex]] = [
                newNestedBlocks[blockIndex],
                newNestedBlocks[blockIndex - 1],
              ];
              return {
                ...block,
                nestedBlocks: newNestedBlocks,
              };
            } else {
              // Recurse deeper
              return {
                ...block,
                nestedBlocks: recursiveMove(
                  block.nestedBlocks || [],
                  remainingPath
                ),
              };
            }
          }
          return block;
        });
      };

      const updated = recursiveMove(prev, containerIndexPath);
      setCodeSnippet(generateCodeFromBlocks(updated)); // ✅ Update snippet
      return recursiveMove(prev, containerIndexPath);
    });
  };

  const moveNestedDown = (containerIndexPath, blockIndex) => {
    setSolutionBlocks((prev) => {
      const recursiveMove = (blocks, path) => {
        const [currentIndex, ...remainingPath] = path;

        return blocks.map((block, index) => {
          if (index === currentIndex) {
            if (remainingPath.length === 0) {
              // Perform the move within the nestedBlocks array
              const newNestedBlocks = [...block.nestedBlocks];
              [newNestedBlocks[blockIndex], newNestedBlocks[blockIndex + 1]] = [
                newNestedBlocks[blockIndex + 1],
                newNestedBlocks[blockIndex],
              ];
              return {
                ...block,
                nestedBlocks: newNestedBlocks,
              };
            } else {
              // Recurse deeper
              return {
                ...block,
                nestedBlocks: recursiveMove(
                  block.nestedBlocks || [],
                  remainingPath
                ),
              };
            }
          }
          return block;
        });
      };

      const updated = recursiveMove(prev, containerIndexPath);
      setCodeSnippet(generateCodeFromBlocks(updated)); // ✅ Update snippet
      return recursiveMove(prev, containerIndexPath);
    });
  };

  const removeFromSolution = (index) => {
    const blockToRemove = solutionBlocks[index];

    // Helper function to recursively collect all nested blocks
    const collectAllNestedBlocks = (block) => {
      const allBlocks = [];
      const collect = (currentBlock) => {
        if (currentBlock.nestedBlocks?.length) {
          currentBlock.nestedBlocks.forEach(collect); // Collect nested blocks
        }
        allBlocks.push({ ...currentBlock, nestedBlocks: [] }); // Reset nested blocks to an empty array
      };
      collect(block);
      return allBlocks;
    };

    // Collect all blocks from the removed block, including itself
    const allBlocksToReturn = blockToRemove.isContainer
      ? collectAllNestedBlocks(blockToRemove)
      : [blockToRemove];

    // Remove the block from the solution section
    const updatedSolutionBlocks = solutionBlocks.filter(
      (_, idx) => idx !== index
    );
    setSolutionBlocks(updatedSolutionBlocks);

    // Add all extracted blocks back to the blocks section
    setBlocks((prevBlocks) => {
      const updatedBlocks = [
        ...prevBlocks,
        ...allBlocksToReturn.filter(
          (block) => !prevBlocks.some((existing) => existing.id === block.id) // Avoid duplicates
        ),
      ];

      // Deduplicate the blocks by ID
      return updatedBlocks.filter(
        (block, idx, self) => idx === self.findIndex((b) => b.id === block.id)
      );
    });

    // Update blocksBySolution for Intermediate/Expert levels
    if (level === "Intermediate" || level === "Expert") {
      const solutionKey = `${level}-${activeSolutionIndex}`;

      setBlocksBySolution((prev) => {
        const currentBlocks = prev[solutionKey] || [];
        const updatedBlocks = [
          ...currentBlocks,
          ...allBlocksToReturn.filter(
            (block) =>
              !currentBlocks.some((existing) => existing.id === block.id)
          ),
        ];

        return {
          ...prev,
          [solutionKey]: updatedBlocks.filter(
            (block, idx, self) =>
              idx === self.findIndex((b) => b.id === block.id)
          ),
        };
      });

      setSolutionBlocksBySolution((prev) => ({
        ...prev,
        [solutionKey]: prev[solutionKey]?.filter(
          (solutionBlock) => solutionBlock.id !== blockToRemove.id
        ),
      }));
    }

    // Update the code snippet
    if (level !== "Pro") {
      setCodeSnippet(generateCodeFromBlocks(updatedSolutionBlocks));
    }
  };

  const mergeNestedBlocks = (existingBlocks, newBlocks) => {
    const mergeBlock = (existingBlock, newBlock) => {
      if (existingBlock.id === newBlock.id && existingBlock.isContainer) {
        const mergedNestedBlocks = [
          ...(existingBlock.nestedBlocks || []),
          ...(newBlock.nestedBlocks || []),
        ];
        // Deduplicate nested blocks by ID
        const deduplicatedNestedBlocks = mergedNestedBlocks.filter(
          (block, index, self) =>
            index === self.findIndex((b) => b.id === block.id)
        );
        return {
          ...existingBlock,
          nestedBlocks: deduplicatedNestedBlocks,
        };
      }
      return newBlock;
    };

    return newBlocks.map((newBlock) => {
      const existingBlock = existingBlocks.find((b) => b.id === newBlock.id);
      return existingBlock ? mergeBlock(existingBlock, newBlock) : newBlock;
    });
  };

  // Function to recursively find and add a block inside a nested container
  const addBlockToNestedContainer = (
    blocks,
    containerIndexPath,
    blockToAdd
  ) => {
    if (!Array.isArray(blocks)) return blocks;

    const recursiveAddBlock = (blocks, path) => {
      const [currentIndex, ...remainingPath] = path;

      return blocks.map((block, index) => {
        if (index === currentIndex) {
          if (remainingPath.length === 0) {
            // Add block to the nestedBlocks array
            return {
              ...block,
              nestedBlocks: [...(block.nestedBlocks || []), blockToAdd],
            };
          } else {
            // Recurse deeper into the nested structure
            return {
              ...block,
              nestedBlocks: recursiveAddBlock(
                block.nestedBlocks || [],
                remainingPath
              ),
            };
          }
        }
        return block;
      });
    };

    return recursiveAddBlock(blocks, containerIndexPath);
  };

  // Store the path to the container block (could be multiple levels deep)
  const handleAddBlockInside = (containerIndexPath) => {
    setSelectedContainerIndex(containerIndexPath); // Path to the nested container
    setIsSelectingBlock(true); // Open block selection modal
  };

  // Function to add a block inside the container
  const addBlockToContainer = (blockId) => {
    const blockToAdd = blocks?.find((b) => b.id === blockId);
    if (!blockToAdd || selectedContainerIndex === null) return;

    setSolutionBlocks((prev) => {
      const updatedSolutionBlocks = addBlockToNestedContainer(
        prev,
        selectedContainerIndex,
        blockToAdd
      );

      // Only update the code snippet if the level is not "Pro"
      if (level !== "Pro") {
        const updatedCode = generateCodeFromBlocks(updatedSolutionBlocks);
        setCodeSnippet(updatedCode);
      }

      // Update solutionBlocksBySolution for Intermediate/Expert levels
      if (level === "Intermediate" || level === "Expert") {
        const solutionKey = `${level}-${activeSolutionIndex}`;
        setSolutionBlocksBySolution((prevSolution) => ({
          ...prevSolution,
          [solutionKey]: updatedSolutionBlocks,
        }));
      }

      return updatedSolutionBlocks;
    });

    // Remove the block from the block library
    setBlocks((prevBlocks) => prevBlocks.filter((b) => b.id !== blockId));

    // Update blocksBySolution for Intermediate/Expert levels
    if (level === "Intermediate" || level === "Expert") {
      const solutionKey = `${level}-${activeSolutionIndex}`;
      setBlocksBySolution((prev) => ({
        ...prev,
        [solutionKey]: prev[solutionKey]?.filter((b) => b.id !== blockId),
      }));
    }

    // Hide the block selection modal
    setIsSelectingBlock(false);
  };

  // Add this function to define styles for different types of blocks
  const getBlockStyle = (type) => {
    const blockStyles = {
      variable: {
        backgroundColor: "#3498db", // Blue for variable blocks
        color: "white",
        borderRadius: 10,
        height: "100%",
        width: "max-content",
        arrowButtonStyle: { backgroundColor: "#3498db", color: "white" }, // For arrow buttons
      },
      loop: {
        backgroundColor: "#ffcc27",
        background:
          "linear-gradient(145deg,rgb(255, 204, 39),rgb(255, 243, 24))", // Yellow for loop blocks
        color: "white",
        borderRadius: 10,
        height: "100%",
        width: "65%",
        // border: "2px solid black",
        arrowButtonStyle: { backgroundColor: "#ffeb3b", color: "black" }, // For arrow buttons
      },
      pattern: {
        backgroundColor: "#8bc34a", // Green for pattern blocks
        color: "white",
        height: "100%",
        width: "max-content",
        borderRadius: 10,
        arrowButtonStyle: { backgroundColor: "#8bc34a", color: "white" }, // For arrow buttons
      },
      action: {
        backgroundColor: "red", // Red for action blocks
        color: "white",
        height: "100%",
        width: "max-content",
        borderRadius: 10,
        arrowButtonStyle: { backgroundColor: "red", color: "white" }, // For arrow buttons
      },
      return: {
        backgroundColor: "black", // Black for return blocks
        color: "white",
        borderRadius: 10,
        width: "max-content",
        arrowButtonStyle: { backgroundColor: "black", color: "white" }, // For arrow buttons
      },
      function: {
        backgroundColor: "purple", // Black for return blocks
        color: "white",
        borderRadius: 10,
        arrowButtonStyle: { backgroundColor: "purple", color: "white" }, // For arrow buttons
      },
      condition: {
        backgroundColor: "orange", // Orange for if blocks
        color: "white",
        borderRadius: 10,
        height: "100%",
        width: "65%",
        arrowButtonStyle: { backgroundColor: "orange", color: "white" }, // For arrow buttons
      },
      insert: {
        backgroundColor: "gray", // Orange for if blocks
        color: "white",
        borderRadius: 10,
        arrowButtonStyle: { backgroundColor: "#f0f0f0", color: "black" }, // For arrow buttons
      },
      default: {
        backgroundColor: "#f0f0f0",
        color: "black",
        borderRadius: 10,
        arrowButtonStyle: { backgroundColor: "#f0f0f0", color: "black" }, // For arrow buttons
      },
    };

    return blockStyles[type] || blockStyles.default;
  };

  // Remove a block from a nested container
  // Remove a block from a nested container
  const removeNestedBlock = (blocks, containerIndexPath, blockIndex) => {
    if (!Array.isArray(blocks)) return blocks;

    const [currentIndex, ...remainingPath] = containerIndexPath;

    return blocks.map((block, index) => {
      if (index === currentIndex) {
        if (remainingPath.length === 0) {
          const removedBlock = block.nestedBlocks[blockIndex];

          // Update solutionBlocksBySolution for Intermediate/Expert
          if (level === "Intermediate" || level === "Expert") {
            const solutionKey = `${level}-${activeSolutionIndex}`;
            setSolutionBlocksBySolution((prev) => {
              const updatedSolutionBlocks = prev[solutionKey]?.map(
                (solutionBlock) =>
                  solutionBlock.id === block.id
                    ? {
                        ...solutionBlock,
                        nestedBlocks: solutionBlock.nestedBlocks.filter(
                          (_, idx) => idx !== blockIndex
                        ),
                      }
                    : solutionBlock
              );

              return {
                ...prev,
                [solutionKey]: updatedSolutionBlocks || [],
              };
            });
          }

          // Add the removed block back to blocksBySolution
          if (level === "Intermediate" || level === "Expert") {
            const solutionKey = `${level}-${activeSolutionIndex}`;
            setBlocksBySolution((prev) => {
              const currentBlocks = prev[solutionKey] || [];
              const updatedBlocks = [
                ...currentBlocks,
                ...(currentBlocks.some((b) => b.id === removedBlock.id)
                  ? []
                  : [removedBlock]),
              ];

              // Deduplicate blocks
              return {
                ...prev,
                [solutionKey]: updatedBlocks.filter(
                  (block, idx, self) =>
                    idx === self.findIndex((b) => b.id === block.id)
                ),
              };
            });
          }

          // Update the code snippet
          setCodeSnippet((prevCode) =>
            generateCodeFromBlocks(
              solutionBlocksBySolution[`${level}-${activeSolutionIndex}`] || []
            )
          );

          // At the target container, remove the nested block
          return {
            ...block,
            nestedBlocks: block.nestedBlocks.filter(
              (_, idx) => idx !== blockIndex
            ),
          };
        } else {
          // Navigate further into nested blocks
          if (level === "Intermediate" || level === "Expert") {
            const solutionKey = `${level}-${activeSolutionIndex}`;
            setSolutionBlocksBySolution((prev) => {
              const updatedSolutionBlocks = prev[solutionKey]?.map(
                (solutionBlock) =>
                  solutionBlock.id === block.id
                    ? {
                        ...solutionBlock,
                        nestedBlocks: removeNestedBlock(
                          solutionBlock.nestedBlocks || [],
                          remainingPath,
                          blockIndex
                        ),
                      }
                    : solutionBlock
              );

              return {
                ...prev,
                [solutionKey]: updatedSolutionBlocks || [],
              };
            });
          }

          return {
            ...block,
            nestedBlocks: removeNestedBlock(
              block.nestedBlocks || [],
              remainingPath,
              blockIndex
            ),
          };
        }
      }
      return block;
    });
  };

  const renderBlock = (
    block,
    index,
    removeFromSolution,
    addBlockToContainer,
    blocksList,
    containerIndexPath = []
  ) => {
    const currentPath = [...containerIndexPath, index];
    const blockStyle = getBlockStyle(block.type);

    // --- utilities ---
    const safeClick = (fn) => (e) => {
      if (solutionCompleted || isDragging) return;
      e.stopPropagation();
      fn(e);
    };

    // cursor bottom-half => "after", top-half => "before"
    const isDropAfter = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      return e.clientY > rect.top + rect.height / 2;
    };

    // prevent dropping a node into its own descendant (would corrupt the tree)
    const isAncestorPath = (ancestor, child) =>
      child.length >= ancestor.length &&
      ancestor.every((v, i) => v === child[i]);

    // insert (before/after) with index shift handling when moving within same parent
    const insertRelative = (
      root,
      parentPath,
      targetIndex,
      dropAfter,
      block,
      movedPath = null
    ) => {
      let insertIndex = targetIndex + (dropAfter ? 1 : 0);

      if (
        movedPath &&
        movedPath.length === parentPath.length &&
        movedPath.slice(0, -1).every((v, i) => v === parentPath[i]) &&
        movedPath[movedPath.length - 1] < insertIndex
      ) {
        insertIndex -= 1;
      }
      return insertIntoParentAtIndex(root, parentPath, insertIndex, block);
    };

    // --- DnD: start/end ---
    const onDragStartSolution = (e) => {
      if (solutionCompleted) return;
      setDragData(e, { source: "solution", path: currentPath });
    };
    const onDragEnd = () => setIsDragging(false);

    // --- DnD: drop "on" a block => insert before/after this block ---
    const handleInsertRelative = (e, parentPath, targetIndex) => {
      if (solutionCompleted) return;
      e.preventDefault();
      e.stopPropagation();

      const data = getDragData(e);
      if (!data) return;

      // prevent illegal self-nesting
      if (
        data.source === "solution" &&
        isAncestorPath(data.path, [...parentPath, targetIndex])
      ) {
        setIsDragging(false);
        return;
      }

      const dropAfter = isDropAfter(e);

      if (data.source === "library") {
        const libBlock = (blocksList || blocks).find((b) => b.id === data.id);
        if (!libBlock) return;

        setBlocks((prev) => prev.filter((b) => b.id !== data.id));
        setSolutionBlocks((prev) => {
          const next = insertRelative(
            prev,
            parentPath,
            targetIndex,
            dropAfter,
            libBlock,
            null
          );
          if (level !== "Pro") setCodeSnippet(generateCodeFromBlocks(next));
          return next;
        });
      } else if (data.source === "solution") {
        setSolutionBlocks((prev) => {
          const { updated, removed } = removeBlockAtPath(prev, data.path);
          if (!removed) return prev;
          
          // Adjust parentPath after removal (indices may have shifted)
          // This is critical when the removed block is above the parent container
          console.log("handleInsertRelative: Adjusting parentPath after removal", {
            originalParentPath: parentPath,
            removedPath: data.path,
            updatedBlocksCount: updated.length
          });
          
          const adjustedParentPath = adjustPathAfterRemoval(parentPath, data.path);
          if (adjustedParentPath === null) {
            console.error("handleInsertRelative: Invalid parentPath after removal - cannot insert");
            // Return updated state (block removed but not moved)
            return updated;
          }
          
          console.log("handleInsertRelative: Using adjusted parentPath", adjustedParentPath);
          
          // The insertRelative function already handles targetIndex adjustment when moving within the same parent
          // via the movedPath parameter. We just need to ensure parentPath is adjusted correctly.
          const next = insertRelative(
            updated,
            adjustedParentPath,
            targetIndex,
            dropAfter,
            removed,
            data.path
          );
          if (level !== "Pro") setCodeSnippet(generateCodeFromBlocks(next));
          return next;
        });
      }

      setIsDragging(false);
    };

    // --- DnD: drop INTO a container's nested area => append inside ---
    const handleDropIntoContainer = (e, containerPath) => {
      if (solutionCompleted) return;
      e.preventDefault();
      e.stopPropagation();

      const data = getDragData(e);
      if (!data) {
        console.warn("handleDropIntoContainer: No drag data found");
        return;
      }

      console.log("handleDropIntoContainer:", { containerPath, data });

      if (data.source === "library") {
        const libBlock = (blocksList || blocks).find((b) => b.id === data.id);
        if (!libBlock) {
          console.warn("handleDropIntoContainer: Block not found in library", data.id);
          return;
        }

        console.log("Appending library block to container:", libBlock.content, "at path:", containerPath);

        setBlocks((prev) => prev.filter((b) => b.id !== data.id));
        setSolutionBlocks((prev) => {
          console.log("Previous solutionBlocks before append:", prev);
          const next = appendIntoContainer(prev, containerPath, libBlock);
          console.log("SolutionBlocks after append:", next);
          if (level !== "Pro") setCodeSnippet(generateCodeFromBlocks(next));
          return next;
        });
      } else if (data.source === "solution") {
        // Guard: don't allow dropping container into its own subtree
        if (isAncestorPath(data.path, containerPath)) {
          console.warn("handleDropIntoContainer: Cannot drop container into its own subtree");
          return;
        }

        console.log("Moving solution block to container at path:", containerPath, "removing block at path:", data.path);

        setSolutionBlocks((prev) => {
          const { updated, removed } = removeBlockAtPath(prev, data.path);
          if (!removed) {
            console.warn("handleDropIntoContainer: Failed to remove block at path", data.path);
            return prev;
          }
          
          // Adjust container path after removal (indices may have shifted)
          // IMPORTANT: We pass the ORIGINAL containerPath and removedPath to calculate the adjustment
          // The adjustment accounts for the index shift that happens after removal
          console.log("Before adjustment - containerPath:", containerPath, "removedPath:", data.path, "updated blocks count:", updated.length);
          const adjustedContainerPath = adjustPathAfterRemoval(containerPath, data.path);
          
          if (adjustedContainerPath === null) {
            console.error("handleDropIntoContainer: Invalid container path after removal - cannot append");
            // Return the updated state (block removed but not moved to container)
            return updated;
          }
          
          console.log("After adjustment - adjustedContainerPath:", adjustedContainerPath, "updated blocks:", updated.map((b, i) => `${i}:${b.content}`));
          
          // Verify the adjusted path is valid before attempting append
          if (adjustedContainerPath.length > 0) {
            const containerIndex = adjustedContainerPath[0];
            if (containerIndex < 0 || containerIndex >= updated.length) {
              console.error(`handleDropIntoContainer: Adjusted path [${adjustedContainerPath.join(',')}] is invalid - index ${containerIndex} out of bounds for array length ${updated.length}`);
              console.error("This suggests the path adjustment logic has a bug. Attempting to find container by ID as fallback.");
              // Fallback: try to find the container by checking all blocks
              // This is a safety measure in case path adjustment fails
              return updated; // For now, return updated (block removed but not moved)
            }
          }
          
          console.log("Removed block:", removed.content, "Appending to container at adjusted path:", adjustedContainerPath);
          
          const next = appendIntoContainer(updated, adjustedContainerPath, removed);
          
          if (next === updated) {
            console.error("handleDropIntoContainer: appendIntoContainer returned unchanged state - append failed!");
            // Block was removed but not added to container, so it's lost
            // Try to add it back to the root to prevent loss
            console.warn("Attempting to recover lost block by adding to root");
            return [...updated, removed];
          }
          console.log("SolutionBlocks after move:", next);
          if (level !== "Pro") setCodeSnippet(generateCodeFromBlocks(next));
          return next;
        });
      }

      setIsDragging(false);
    };

    // --- UI specifics from your version ---
    const addButtonStyle =
      block.type === "variable" || block.type === "return"
        ? {
            marginLeft: "10px",
            alignSelf: "flex-end",
            fontSize: "12px",
            padding: "5px 10px",
            height: "fit-content",
          }
        : {
            marginTop: "10px",
            alignSelf: "center",
            fontSize: "14px",
            padding: "8px 12px",
          };

    const showAddBlockButton =
      block.type === "variable" || block.type === "return"
        ? block.nestedBlocks?.length === 0
        : true;

    // --- Container block ---
    // --- Container block ---
    if (block.isContainer) {
      return (
        <div
          key={index}
          style={{
            ...blockStyle,
            display: "flex",
            flexDirection: "column",
            padding: "10px",
            marginBottom: "0px",
            height: "fit-content",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "13px",
            fontWeight: 600,
            borderRadius: "14px",
            border: "none",
            cursor: "default",
            transition: "all 0.25s ease-in-out",
            boxShadow:
              "inset 0 -4px 0 rgba(0, 0, 0, 0.2), 0 6px 10px rgba(0, 0, 0, 0.15)",
            minWidth: "120px",
            maxWidth: "300px",
            textAlign: "center",
            whiteSpace: "pre-wrap",
          }}
          // NOTE: no draggable on the whole container
          onClick={safeClick(() => {
            if (containerIndexPath.length === 0) {
              removeFromSolution(index);
            } else {
              setSolutionBlocks((prevSolutionBlocks) => {
                const updatedBlocks = removeNestedBlock(
                  prevSolutionBlocks,
                  containerIndexPath,
                  index
                );
                setCodeSnippet(generateCodeFromBlocks(updatedBlocks));
                return updatedBlocks;
              });
              setBlocks((prevBlocks) => [...prevBlocks, block]);
            }
          })}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "move";
          }}
          onDrop={(e) => {
            // When dropping on a container block, always append inside it
            // This is more intuitive than inserting before/after
            e.preventDefault();
            e.stopPropagation();
            handleDropIntoContainer(e, currentPath);
          }}
        >
          {/* Drag handle for moving the container itself */}
          <div
            className="block-content-title"
            style={{
              cursor: solutionCompleted ? "default" : "grab",
              userSelect: "none",
            }}
            draggable={!solutionCompleted}
            onDragStart={(e) => {
              if (solutionCompleted) return;
              // This drag is for the container itself
              setDragData(e, { source: "solution", path: currentPath });
            }}
            onDragEnd={onDragEnd}
            onDragOver={(e) => {
              // Prevent the drag over from bubbling to parent
              e.stopPropagation();
            }}
          >
            {block.content}
          </div>

          {/* Nested area = its own drop zone (append INSIDE) */}
          <div
            style={{
              padding: "10px",
              marginTop: "0px",
              display: "flex",
              flexDirection: "column",
              marginLeft: "0px",
              marginBottom: "0px",
              borderRadius: "8px",
              fontFamily: "'Fira Code', monospace",
              fontSize: "12px",
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = "move";
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDropIntoContainer(e, currentPath);
            }}
          >
            {(block.nestedBlocks || []).map((nestedBlock, nestedIndex) => {
              const nestedBlockStyle = getBlockStyle(nestedBlock.type);

              // Container’s path is the parent for sibling reordering
              const parentPathForSiblings = currentPath;
              const childPath = [...currentPath, nestedIndex];

              return (
                <div
                  className="nested-block"
                  key={nestedIndex}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                    width: "max-content",
                    maxWidth: "300px",
                    padding: "5px",
                    borderRadius: "10px",
                    transition: "all 0.2s ease-in-out",
                  }}
                  // NOTE: do NOT make the row draggable
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  // Drop before/after this row = reorder within container
                  onDrop={(e) =>
                    handleInsertRelative(e, parentPathForSiblings, nestedIndex)
                  }
                >
                  <div
                    className="arrow-buttons"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "40px",
                      alignItems: "center",
                    }}
                  >
                    <button
                      style={{
                        cursor: nestedIndex === 0 ? "not-allowed" : "pointer",
                        padding: "5px",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: "16px",
                        backgroundColor:
                          nestedIndex === 0
                            ? "lightgray"
                            : nestedBlockStyle.backgroundColor,
                        color: nestedBlockStyle.color,
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.52)",
                        border: `1px solid ${nestedBlockStyle.color}`,
                      }}
                      onClick={safeClick(() =>
                        moveNestedUp(currentPath, nestedIndex)
                      )}
                      disabled={nestedIndex === 0}
                    >
                      ↑
                    </button>
                    <button
                      style={{
                        cursor:
                          nestedIndex === block.nestedBlocks?.length - 1
                            ? "not-allowed"
                            : "pointer",
                        padding: "5px",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: "16px",
                        backgroundColor:
                          nestedIndex === block.nestedBlocks?.length - 1
                            ? "lightgray"
                            : nestedBlockStyle.backgroundColor,
                        color: nestedBlockStyle.color,
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.52)",
                        border: `1px solid ${nestedBlockStyle.color}`,
                      }}
                      onClick={safeClick(() =>
                        moveNestedDown(currentPath, nestedIndex)
                      )}
                      disabled={nestedIndex >= block.nestedBlocks?.length - 1}
                    >
                      ↓
                    </button>
                  </div>

                  {/* The child owns its drag; pass container path so child computes its own full path */}
                  {renderBlock(
                    nestedBlock,
                    nestedIndex,
                    removeFromSolution,
                    addBlockToContainer,
                    blocksList,
                    currentPath
                  )}
                </div>
              );
            })}

            {showAddBlockButton && (
              <button
                style={{
                  ...addButtonStyle,
                  backgroundColor: "lightgray",
                  color: "black",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddBlockInside(currentPath);
                }}
              >
                ➕ Add Block Inside
              </button>
            )}
          </div>
        </div>
      );
    }

    // --- Non-container block ---
    // --- Non-container block ---
    return (
      <div
        key={index}
        className="non-container-block"
        style={{
          ...blockStyle,
          padding: "10px",
          marginBottom: "5px",
          cursor: solutionCompleted ? "default" : "grab",
        }}
        draggable={!solutionCompleted}
        onDragStart={(e) => {
          if (solutionCompleted) return;
          // currentPath here is [...containerIndexPath, index] for nested children
          setDragData(e, { source: "solution", path: currentPath });
        }}
        onDragEnd={onDragEnd}
        onClick={safeClick(() => {
          if (containerIndexPath.length === 0) {
            removeFromSolution(index);
          } else {
            setSolutionBlocks((prevSolutionBlocks) => {
              const updatedBlocks = removeNestedBlock(
                prevSolutionBlocks,
                containerIndexPath,
                index
              );
              setCodeSnippet(generateCodeFromBlocks(updatedBlocks));
              return updatedBlocks;
            });
            setBlocks((prevBlocks) => [...prevBlocks, block]);
          }
        })}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = "move";
        }}
        onDrop={(e) => handleInsertRelative(e, containerIndexPath, index)}
      >
        {block.content}
      </div>
    );
  };

  const functionStructure = useMemo(() => {
    if (!solutions[activeSolutionIndex]?.solutionCode) {
      console.error("solutionCode is undefined or null");
      return { opening: "", closing: "" }; // Provide default values to prevent errors
    }

    const lines = solutions[activeSolutionIndex]?.solutionCode.split("\n");
    const openingLine = lines[0] || ""; // Safeguard in case lines[0] is undefined

    // Detect if the code is Python by checking the presence of `def` in the first line
    const isPython = openingLine.trim().startsWith("def");

    return {
      opening: openingLine,
      closing: isPython ? "" : lines[lines.length - 1] || "", // For Python, no closing line
    };
  }, [solutions, activeSolutionIndex]);

  const extractFunctionStructure = (solutionCode) => {
    // if (level === "Pro") {
    //   return { opening: "", closing: "" }; // No function structure for Pro level
    // }

    if (!solutionCode) {
      return { opening: "", closing: "" };
    }

    const lines = solutionCode.split("\n");
    const openingLine = lines[0] || "";
    const closingLine = lines[lines.length - 1]?.trim();
    const isPython = openingLine.trim().startsWith("def");

    return {
      opening: openingLine,
      closing: isPython || closingLine !== "}" ? "" : closingLine,
    };
  };

  const generateDynamicCode = (solutionCode, blocks) => {
    const { opening, closing } = extractFunctionStructure(solutionCode);
    const generatedCode = generateCodeFromBlocks(blocks, 1); // Generate block code

    // Adjust formatting based on whether there's a closing line
    return closing
      ? `${opening}\n  ${generatedCode}\n${closing}`
      : `${opening}\n  ${generatedCode}`;
  };

  // Recursive function to generate code from blocks, with correct indentation
  const generateCodeFromBlocks = (blocks, indentationLevel = 0) => {
    let code = "";

    blocks?.forEach((block) => {
      const indent = "  ".repeat(indentationLevel); // Two spaces for each indentation level
      if (block.isContainer) {
        // Handle container blocks (e.g., loops or conditionals)
        const nestedCode = generateCodeFromBlocks(
          block.nestedBlocks,
          indentationLevel + 1
        );
        code += `${indent}${block.code
          .replace("// Insert blocks here", nestedCode)
          .trim()}\n`;
      } else {
        // Handle non-container blocks (e.g., variables, actions)
        code += `${indent}${block.code.trim()}\n`;
      }
    });

    return code.trim(); // Trim the final code to remove any excess whitespace
  };

  const resetBlocks = (matchedSolution) => {
    // Get the block content from the matched solution to filter them out
    const usedBlocks = extractUsedBlocksFromSolution(
      matchedSolution.solutionCode
    );

    // Filter out the used blocks from the available blocks
    const remainingBlocks = blocks.filter(
      (block) => !usedBlocks.includes(block.content)
    );

    // Reset the solution blocks (clear the workspace)
    setSolutionBlocks([]);

    // Update the available blocks to remove the used ones
    setBlocks(remainingBlocks);

    // Reset the code snippet area
    setCodeSnippet("");
  };

  // This is a helper function that extracts the blocks that were used in the solution
  const extractUsedBlocksFromSolution = (solutionCode) => {
    // Normalize the solution code to extract the relevant block contents
    const usedBlockContents = [];

    // Example logic: Check each block's code against the solution code
    blocks.forEach((block) => {
      if (solutionCode.includes(block.code)) {
        usedBlockContents.push(block.content); // Collect the block's content
      }
    });

    return usedBlockContents;
  };

  const navigate = useNavigate(); // Initialize navigate

  // Function to navigate to the next problem
  const navigateToNextProblem = async (level) => {
    try {
      const response = await axios.get(`${baseUrl}/problems?level=${level}`);
      const nextProblem = response.data.find((p) => p.id > problem.id); // Find the next problem

      if (nextProblem) {
        navigate(`/problems/${nextProblem.id}`, {
          state: { level },
        });
      } else {
        alert("No more problems available");
      }
    } catch (error) {
      console.error("Error fetching the next problem:", error);
    }
  };

  const launchConfetti = () => {
    const canvas = document.getElementById("confetti-canvas");
    const myConfetti = confetti.create(canvas, {
      resize: true,
      useWorker: true,
    });

    const duration = 1 * 1000;
    const animationEnd = Date.now() + duration;
    const colors = ["#ff0", "#ff4500", "#00c3ff", "#ff1493"];

    const frame = () => {
      myConfetti({
        particleCount: 5,
        angle: 60,
        spread: 75,
        origin: { x: 0 },
        colors: colors,
      });

      myConfetti({
        particleCount: 5,
        angle: 120,
        spread: 75,
        origin: { x: 1 },
        colors: colors,
      });

      if (Date.now() < animationEnd) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  };

  const checkSolution = async () => {
    setIsChecking(true); // Start loading

    // 🧠 Limit for FREE users (run once per day)
    if (user?.role === "FREE_USER") {
      const today = new Date().toISOString().split("T")[0]; // 'YYYY-MM-DD'
      const lastRunDate = localStorage.getItem("lastRunCodeDate");

      if (lastRunDate === today) {
        Swal.fire({
          title: "Daily Limit Reached",
          text: "Free users can only run code once per day. Upgrade for unlimited runs!",
          icon: "warning",
          confirmButtonText: "Upgrade",
          showCancelButton: true,
          cancelButtonText: "Maybe later",
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/membership"); // or your upgrade page
          }
        });

        return; // ⛔️ Stop execution
      } else {
        localStorage.setItem("lastRunCodeDate", today); // ✅ Save today's usage
      }
    }

    const normalize = (code) => code.replace(/\s+/g, " ").trim();

    const { opening, closing } = extractFunctionStructure(
      solutions[activeSolutionIndex]?.solutionCode
    );

    const fullUserSolution = codeSnippet.includes(opening)
      ? codeSnippet.trim()
      : `${opening}\n${codeSnippet}\n${closing}`.trim();

    const normalizedUserSolution = normalize(fullUserSolution);

    const userId = localStorage.getItem("userId");
    const problemId = problem.id;
    const solutionId = solutions[activeSolutionIndex]?.id;
    const solutionLevel = level;

    const requestData = {
      language: selectedLanguage,
      code: normalizedUserSolution,
      problemTitle: problem.title,
      level: solutionLevel,
      functionName: problem.functionName,
      inputVars: problem.inputVars, // ✅ Pass inputVars dynamically
      returnType: problem.returnType, // ✅ Pass returnType dynamically
      testCases: problem.testCases, // ✅ Use testCases directly from the database
      solutionCode: solutions[activeSolutionIndex]?.solutionCode,
    };

    try {
      const response = await axios.post(`${codeRunnerUrl}/run`, requestData);
      const { output, testCaseResults } = response.data;

      // 🧠 Use parsed results from backend if available (e.g. timeout)
      const parsedResults = testCaseResults
        ? testCaseResults
        : parseTestResults(output, problem.testCases);

      const allPassed = parsedResults.every((test) => test.passed);

      setTestResults(parsedResults);
      setAllTestsPassed(allPassed);

      if (output.includes("took too long")) {
        Swal.fire({
          title: "Infinite Loop?",
          text: "Your code took too long to run. This usually means there's an infinite loop or excessive computation.",
          icon: "warning",
          confirmButtonText: "OK",
        });
      }

      if (allPassed) {
        // launchConfetti(); // 🎉 Trigger confetti effect
        handleCorrectSolution();

        if (userId) {
          try {
            await axios.put(
              `${baseUrl}/progress/update/${userId}/${problemId}`,
              {
                completed: true,
                solutionId: solutionId || null,
                level: solutionLevel.toUpperCase(),
              }
            );
          } catch (error) {
            console.error("❌ Error updating UserProgress:", error);
          }
        }
      } else {
        handleIncorrectSolution();
      }
    } catch (error) {
      console.error("❌ Error checking solution via backend:", error);
    } finally {
      setIsChecking(false);
    }
  };

  const parseTestResults = (output, testCases) => {
    if (!output || typeof output !== "string") return [];

    const sanitizedOutput = output.replace(/[^\x20-\x7E\n]/g, "").trim();

    return testCases.map((test, index) => {
      const input = test.inputs ? JSON.stringify(test.inputs) : "Unknown Input";

      const regexPattern = new RegExp(`Test ${index + 1} Output:\\s*(.*)`);
      const match = regexPattern.exec(sanitizedOutput);

      let actualOutput = "Error: No Output Found";
      if (match && match[1]) {
        actualOutput = match[1].trim();
      }

      // Normalize helper
      const normalizeOutput = (str) =>
        str
          .replace(/'/g, '"')
          .replace(/\s*,\s*/g, ",")
          .trim();

      const normalizedActual = normalizeOutput(actualOutput);

      // ✅ Normalize all possible expected outputs (support string or array)
      const expected = Array.isArray(test.expectedOutput)
        ? test.expectedOutput.map(normalizeOutput)
        : [normalizeOutput(test.expectedOutput)];

      // ✅ Check if any expected matches the actual
      // ✅ Check if any expected matches the actual
      const passed = expected.some((expectedStr) => {
        // First, try to parse both sides as numbers for numeric equality
        const expectedNum = parseFloat(expectedStr);
        const actualNum = parseFloat(normalizedActual);

        const bothAreNumbers = !isNaN(expectedNum) && !isNaN(actualNum);

        if (bothAreNumbers) {
          return expectedNum === actualNum;
        }

        // Fallback: string-based match
        return expectedStr === normalizedActual;
      });

      return {
        input,
        expectedOutput: expected.join(" or "),
        actualOutput: normalizedActual,
        passed,
      };
    });
  };

  const showTestResultsPopup = (testCaseResults, allPassed) => {
    let resultHTML = "<h3>Test Results</h3><ul style='text-align: left;'>";

    testCaseResults.forEach((test, index) => {
      resultHTML += `
          <li>
              <strong>Test ${index + 1}:</strong> ${test.passed ? "✅ Passed" : "❌ Failed"}<br/>
              <strong>Input:</strong> ${test.input}<br/>
              <strong>Expected:</strong> ${test.expectedOutput}<br/>
              <strong>Received:</strong> ${test.actualOutput}
          </li>
          <br/>
      `;
    });

    resultHTML += "</ul>";

    Swal.fire({
      title: allPassed ? "🎉 All Tests Passed!" : "⚠️ Some Tests Failed",
      html: resultHTML,
      icon: allPassed ? "success" : "error",
      confirmButtonText: "Okay",
    });
  };

  const handleCorrectSolution = async (type, solution = {}) => {
    // 🎉 Fire confetti
    launchConfetti();
    // Resize the blocks section after solving correctly
    setTimeout(() => {
      setSplitSizes([35, 65]);
    }, 1000);

    const optimalSolution = solutions.find((sol) => sol.isOptimal);
    const isBeginnerLevel = level === "Beginner";
    const isProLevel = level === "Pro";
    const isIntermediateOrExpert =
      level === "Intermediate" || level === "Expert";

    const solutionId = solution?.id || solutions[activeSolutionIndex]?.id;

    // ✅ Update progress depending on level
    if (updateProgress) {
      const normalizedLevel = level.toUpperCase();

      if (isBeginnerLevel || isProLevel) {
        updateProgress({ [normalizedLevel]: true });
      } else if (isIntermediateOrExpert && solutionId) {
        updateProgress((prev) => {
          const current = prev?.[normalizedLevel] || {
            count: 0,
            solutionIds: [],
          };
          return {
            [normalizedLevel]: {
              count: current.count + 1,
              solutionIds: [...new Set([...current.solutionIds, solutionId])],
            },
          };
        });
      }
    }

    // // 🎉 Success modal
    // await Swal.fire({
    //   title: "🎉 Great job!",
    //   text: "Your solution is correct.",
    //   icon: "success",
    //   confirmButtonText: "Keep Going",
    // });

    // Stats logging
    const isOptimal = isBeginnerLevel
      ? true
      : solutions[activeSolutionIndex]?.isOptimal || false;
  };

  // Handle incorrect solutions
  const handleIncorrectSolution = () => {
    // Swal.fire({
    //   title: "Try Again",
    //   text: "Your solution is not correct. Keep trying!",
    //   icon: "error",
    //   confirmButtonText: "Got it",
    // });

    setShowHintButton(true); // Show the button
    setHintUsed(false); // Reset hint usage if trying again
    setShakeEditor(true);
    setShowErrorToast(true);

    setTimeout(() => {
      setTestCaseSizes([30, 70]);
    }, 1500);

    setTimeout(() => {
      setShakeEditor(false);
      setShowErrorToast(false);
    }, 4000);
  };

  const getDifficultyStyle = (difficulty) => {
    switch (difficulty) {
      case "EASY":
        return "difficulty easy";
      case "MEDIUM":
        return "difficulty medium";
      case "HARD":
        return "difficulty hard";
      default:
        return "difficulty";
    }
  };

  const codeMirrorValue = useMemo(() => {
    if (level === "Pro") {
      return `${functionStructure.opening}\n${codeSnippet}`;
    } else {
      return generateDynamicCode(
        solutions[activeSolutionIndex]?.solutionCode,
        solutionBlocks
      );
    }
  }, [level, solutions, activeSolutionIndex, codeSnippet, solutionBlocks]);

  return (
    <div className={`drag-container ${shakeEditor ? "shake" : ""}`}>
      <div className="level-switcher">
        {/* <LevelSwitcher
          level={level}
          handleLevelChange={handleLevelChange}
          renderSolutionTabs={renderSolutionTabs}
        /> */}
      </div>
      <Split
        className="split"
        sizes={[25, 75]} // Initial sizes: 20% left, 80% right
        minSize={[200, 300]} // Left pane can be hidden, right min width is 300px
        gutterSize={10} // Width of the gutter
        snapOffset={10} // Snaps when resizing
        expandToMin={false}
      >
        <div className="left-pane">
          <ProblemSection
            problem={problem}
            level={level}
            handleLevelChange={handleLevelChange}
            user={user}
            // renderSolutionTabs={renderSolutionTabs}
          />
        </div>
        <div className="right-pane">
          <Split
            className="horizontal-split"
            sizes={level === "Pro" ? [0, 100] : [50, 50]} // Collapse left pane in Code Mode
            minSize={[level === "Pro" ? 0 : 200, 300]} // Set min size conditionally
          >
            <motion.div
              className="left-section"
              initial={{ width: "25%" }}
              animate={{ width: level === "Pro" ? "0%" : "75%" }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              style={{ overflow: "hidden" }} // Hide overflow when closed
            >
              <Split
                className="blocks-split"
                direction="vertical"
                sizes={splitSizes}
                onDragEnd={(sizes) => setSplitSizes(sizes)}
                // sizes={[40, 60]}
                // minSize={[100, 100]}
                // height="auto"
              >
                <div
                  className="solution-section"
                  style={{ height: "auto" }}
                  onDragOver={(e) => {
                    if (solutionCompleted) return;
                    e.preventDefault();
                    e.stopPropagation(); // <-- add this
                    e.dataTransfer.dropEffect = "move";
                  }}
                  // root solution drop area handler
                  onDrop={(e) => {
                    if (solutionCompleted) return;
                    e.preventDefault();
                    e.stopPropagation();

                    const data = getDragData(e);
                    if (!data) return;

                    if (data.source === "library") {
                      const libBlock = blocks.find((b) => b.id === data.id);
                      if (!libBlock) return;

                      setBlocks((prev) => prev.filter((b) => b.id !== data.id));
                      setSolutionBlocks((prev) => {
                        const next = [...prev, libBlock];
                        if (level !== "Pro")
                          setCodeSnippet(generateCodeFromBlocks(next));
                        return next;
                      });
                    } else if (data.source === "solution") {
                      setSolutionBlocks((prev) => {
                        const { updated, removed } = removeBlockAtPath(
                          prev,
                          data.path
                        );
                        if (!removed) return prev;
                        const next = [...updated, removed]; // append to root
                        if (level !== "Pro")
                          setCodeSnippet(generateCodeFromBlocks(next));
                        return next;
                      });
                    }

                    setIsDragging(false);
                  }}
                >
                  <SolutionSection
                    showCompletionModal={showCompletionModal}
                    setShowCompletionModal={setShowCompletionModal}
                    level={level}
                    handleLevelChange={handleLevelChange}
                    solutionBlocks={solutionBlocks}
                    renderBlock={renderBlock}
                    removeFromSolution={removeFromSolution}
                    handleAddBlockInside={handleAddBlockInside}
                    blocks={blocks}
                    moveBlockUp={moveBlockUp}
                    moveBlockDown={moveBlockDown}
                    showHintButton={showHintButton}
                    fetchHint={fetchHint}
                    getBlockStyle={getBlockStyle}
                    solutionCompleted={solutionCompleted}
                    solutionName={activeSolutionName}
                  />
                </div>
                <div
                  className="blocks-section"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation(); // <— add this
                    e.dataTransfer.dropEffect = "move";
                  }}
                  // in the blocks-section onDrop
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const data = getDragData(e);
                    if (!data || data.source !== "solution") return;

                    setSolutionBlocks((prev) => {
                      const { updated, removed } = removeBlockAtPath(
                        prev,
                        data.path
                      );
                      if (!removed) return prev;

                      const toReturn = removed.isContainer
                        ? flattenBlock(removed)
                        : [removed];

                      if (level !== "Pro")
                        setCodeSnippet(generateCodeFromBlocks(updated));

                      setBlocks((libPrev) =>
                        dedupById([...libPrev, ...toReturn])
                      );
                      return updated;
                    });

                    setIsDragging(false);
                  }}
                >
                  {/* <LevelSwitcher 
                    level={level}
                    handleLevelChange={handleLevelChange}
                    renderSolutionTabs={renderSolutionTabs}
                  /> */}

                  {/* Render the "Reveal Blocks" button for Pro level */}
                  {/* {level === "Pro" && !areBlocksVisible && (
                    <button
                      className="reveal-blocks-button"
                      onClick={handleRevealBlocks}
                    >
                      Reveal Blocks
                    </button>
                  )} */}

                  {/* Render level switcher here */}
                  {/* {renderLevelSwitcher()} */}
                  <BlocksSection
                    areBlocksVisible={areBlocksVisible}
                    blocks={blocks}
                    blockHighlightClass={blockHighlightClass}
                    highlightedBlock={highlightedBlock}
                    addToSolution={addToSolution}
                    level={level}
                    handleLevelChange={handleLevelChange}
                    showHintButton={showHintButton}
                    hintUsed={hintUsed}
                    solutionCompleted={solutionCompleted}
                    solutions={solutions}
                    activeSolutionIndex={activeSolutionIndex}
                    problem={problem}
                    codeSnippet={codeSnippet}
                    onHintClick={() => {
                      fetchHint();
                      setHintUsed(true);
                    }}
                    resetWorkspace={resetWorkspace}
                    // renderSolutionTabs={renderSolutionTabs}
                  />
                </div>
              </Split>
            </motion.div>
            <motion.div
              className="right-section"
              initial={{ width: "75%" }}
              animate={{ width: level === "Pro" ? "100%" : "75%" }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            >
              <Split
                className="code-output-split"
                direction="vertical"
                sizes={testCaseSizes}
                onDragEnd={(sizes) => setTestCaseSizes(sizes)}
                // maxSize={[500, 500]}
                minSize={[100, 100]}
              >
                <div className={`code-section`}>
                  <CodeEditor
                    level={level}
                    generateDynamicCode={generateDynamicCode}
                    solutions={solutions}
                    activeSolutionIndex={activeSolutionIndex}
                    solutionBlocks={solutionBlocks}
                    options={options}
                    codeSnippet={codeSnippet}
                    setCodeSnippet={setCodeSnippet}
                    // setProCodeSnippet={setProCodeSnippet}
                    selectedText={selectedText}
                    buttonPosition={buttonPosition}
                    handleWhatDoesThisMean={handleWhatDoesThisMean}
                    checkSolution={checkSolution}
                    handleSelectionChange={handleSelectionChange}
                    problem={problem}
                    selectedLanguage={selectedLanguage}
                    extractFunctionStructure={extractFunctionStructure}
                  />
                </div>
                <div className="output-section">
                  <TestCases
                    testResults={testResults}
                    currentTestIndex={currentTestIndex}
                    setCurrentTestIndex={setCurrentTestIndex}
                    allTestsPassed={allTestsPassed}
                    feedbackMessage={feedbackMessage}
                    problem={problem}
                    isChecking={isChecking}
                    checkSolution={checkSolution}
                  />
                </div>
              </Split>
            </motion.div>
          </Split>
        </div>
        {showErrorToast && (
          <div className="error-toast">
            ❌ Your solution is not correct. Keep trying!
          </div>
        )}
      </Split>

      {/* Modal/Dropdown for block selection when adding inside a container */}
      {isSelectingBlock && (
        <div
          className="block-selection-modal"
          style={{
            position: "fixed",
            top: "40%",
            left: "70%",
            transform: "translate(-50%, -50%)",
            width: "400px",
            maxWidth: "90%",
            maxHeight: "70vh",
            background: "#fefefe",
            padding: "25px 20px 20px 20px",
            borderRadius: "16px",
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.26)",
            border: "2px solid #d0d7de", // 🟦 Soft border added here
            zIndex: 1000,
            overflowY: "auto",
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          {/* Close Button */}
          <button
            onClick={() => setIsSelectingBlock(false)}
            style={{
              position: "absolute",
              top: "10px",
              right: "15px",
              fontSize: "18px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#999",
            }}
            aria-label="Close"
          >
            ✖
          </button>

          <h3 style={{ marginBottom: "15px", fontSize: "18px", color: "#333" }}>
            Select a Block to Add Inside
          </h3>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {blocks?.length > 0 ? (
              blocks.map((block) => {
                const { backgroundColor, color } = getBlockStyle(block.type);
                return (
                  <button
                    key={block.id}
                    onClick={() => addBlockToContainer(block.id)}
                    style={{
                      padding: "10px 14px",
                      borderRadius: "12px",
                      border: "none",
                      backgroundColor,
                      color,
                      fontWeight: 600,
                      fontSize: "13px",
                      cursor: "pointer",
                      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {block.content}
                  </button>
                );
              })
            ) : (
              <p style={{ color: "#666" }}>No blocks available to add</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DragAndDropWorkspace;
